import { BACKEND_ERROR_TYPES, RATE_LIMIT_TYPES } from '@/types/error';
import axios, { AxiosError } from 'axios';

import { API_ROUTES_TO_IGNORE } from '@/shared/constants/sentry-constants';

interface BackendError {
	error_code: number;
	error_type: BACKEND_ERROR_TYPES;
	message: string;
}

export type RateLimitData = {
	error_type: BACKEND_ERROR_TYPES.FREEMIUM_LIMIT_REACHED;
	limit_type: RATE_LIMIT_TYPES;
	limit: number;
	current_count: number;
	credit?: number;
	credit_limit?: number;
	credits_used?: number;
	credits_needed?: number;
};

export function isAbortedRequest(error: unknown): boolean {
	if (axios.isCancel(error)) {
		return true;
	}

	if (axios.isAxiosError(error)) {
		if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
			return true;
		}
	}

	return false;
}

export const parseAxiosErrorsForAccessDenied = (
	error: AxiosError
): {
	workspace: boolean;
	feature: boolean;
	message: string;
} | null => {
	const errorData = error?.response?.data as BackendError;
	if (!errorData) return null;

	if (errorData?.error_type === BACKEND_ERROR_TYPES.FEATURE_ACCESS_DENIED) {
		return {
			workspace: false,
			feature: true,
			message: errorData.message,
		};
	}
	if (errorData.error_type === BACKEND_ERROR_TYPES.WORKSPACE_ACCESS_DENIED) {
		return {
			workspace: true,
			feature: false,
			message: errorData.message,
		};
	}
	if (errorData.error_type === BACKEND_ERROR_TYPES.FORM_ACCESS_DENIED) {
		return {
			workspace: false,
			feature: true,
			message: errorData.message,
		};
	}

	return null;
};

export function checkIfNetworkError(error: AxiosError): boolean {
	// Aborted requests have request but no response - don't treat as network error
	if (isAbortedRequest(error)) {
		return false;
	}

	return (
		error?.code === 'ECONNREFUSED' ||
		error?.code === 'ECONNRESET' ||
		(error?.request && !error?.response)
	);
}

/**
 * Extracts rate limit data from a 429 error response
 * Used across the platform to handle freemium limit errors consistently
 * @param error - The error to extract rate limit data from
 * @returns Rate limit data if it's a freemium limit error, null otherwise
 */
/**
 * True when a 429 is an expected freemium limit rather than a fault — any limit type.
 * The single definition of this rule; callers must not re-derive it, since the copies
 * previously disagreed about whether INTERVIEWS counted as expected.
 */
export function isExpectedRateLimit(error: unknown): boolean {
	return extractRateLimitData(error) !== null;
}

/** True when the URL matches a route excluded from Sentry reporting (e.g. pollers). */
export function shouldIgnoreRouteForSentry(url: string | undefined): boolean {
	if (!url) return false;
	return API_ROUTES_TO_IGNORE.some((route) => url.includes(route));
}

export function extractRateLimitData(error: unknown): RateLimitData | null {
	if (!axios.isAxiosError(error) || error.response?.status !== 429) {
		return null;
	}

	const errorData = error.response?.data as RateLimitData;

	// Return rate limit data if it's a freemium limit error
	if (errorData?.error_type === BACKEND_ERROR_TYPES.FREEMIUM_LIMIT_REACHED) {
		return {
			error_type: BACKEND_ERROR_TYPES.FREEMIUM_LIMIT_REACHED,
			limit_type: (errorData.limit_type as RATE_LIMIT_TYPES) || RATE_LIMIT_TYPES.AI_CREDITS,
			limit: errorData.limit ?? 0,
			current_count: errorData.current_count ?? 0,
			credit: errorData.credit ?? 0,
		};
	}

	return null;
}
